import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, resolveComponent as _resolveComponent, createVNode as _createVNode, createElementVNode as _createElementVNode, createTextVNode as _createTextVNode, withCtx as _withCtx, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, resolveDirective as _resolveDirective, withDirectives as _withDirectives, normalizeProps as _normalizeProps, guardReactiveProps as _guardReactiveProps, mergeProps as _mergeProps, withModifiers as _withModifiers, createElementBlock as _createElementBlock } from "vue"

import { ref, computed, onMounted } from 'vue'
import logo from '@/assets/maropost-logo.svg'
import type { VAutocomplete } from 'vuetify/components'
import { Account, useAccountService } from '@/lib/services'
// @ts-ignore - lodash types not available
import { debounce } from 'lodash'
import { useRoute } from 'vue-router'
import background from '@/assets/images/background.jpeg'

const itemsPerPage = 10

export default /*@__PURE__*/_defineComponent({
  __name: 'SelectAccountPage',
  setup(__props) {

const route = useRoute()

const style = {
  backgroundImage: `url('${background}')`
}

// Simple validation function
const required = (fieldName: string) => {
  return (value: Account | null) => {
    if (!value) {
      return `${fieldName} is required`
    }
    return true
  }
}

const autocompleteRef = ref<InstanceType<typeof VAutocomplete> | null>(null)
const model = ref<Account | null>(null)
const $account = useAccountService()
const isFormValid = ref(false)
const isLoading = ref(false)
const items = ref<Account[]>([])
const hasItems = ref(false)
const search = ref('')
const page = ref(1)
const selecting = ref(false)
const accountId = ref(0)

onMounted(() => {
  if (route.query.accountId) {
    accountId.value = Number(route.query.accountId)
  }
})

const onModelUpdate = () => {
  selecting.value = true

  // reset after next tick or short delay
  setTimeout(() => {
    selecting.value = false
  }, 600)
}

/**
 * Triggered when the intersection observer detects the end of the item list.
 * Loads more items if the observer indicates the component is intersecting.
 *
 * @param {boolean} isIntersecting - Indicates if the component is currently intersecting.
 */
const onIntersect = (isIntersecting: boolean) => {
  if (isIntersecting && !$account.isFetching.value && hasItems.value) {
    loadMoreItems()
  }
}

const isLoadMoreEnabled = computed(() => {
  return (
    hasItems.value &&
    !$account?.isFetching?.value 
  )
})

/**
 * Loads more items if available and not already loading.
 */
const loadMoreItems = async () => {
  if (
    !hasItems.value ||
    $account.isFetching.value 
  )
    return // Prevent multiple requests
  page.value++
  await fetchItems()
}

/**
 * Fetches items from the API using the provided service.
 * Constructs the request parameters based on current state.
 */
const fetchItems = async () => {
  const searchKeyword = items.value.find((item) => {
      return item['name'] === search.value
    })? ''
      : search.value

  const config = {
    abort: true,
    params: {
      per_page: itemsPerPage, // Set limit for items per page
      page: page.value,
      name_cont: searchKeyword
    }
  }

  try {
    isLoading.value = true
    await $account.findAll(config)
    const data = ($account.items.value || []) as Account[]
    items.value = page.value == 1 ? [...data] : [...items.value, ...data]
    if (search.value && autocompleteRef.value) {
      autocompleteRef.value.menu = true
    }
    hasItems.value = data.length >= itemsPerPage
  } catch (error: unknown) {
    console.error('Error fetching items:', error)
  } finally {
    isLoading.value = false
  }
}
function onFocusHandler(isFocused: boolean) {
  isFocused && items.value.length < 10 && !$account.isFetching.value && fetchItems()
}
/**
 * Triggered when the search input changes. Resets the page to 1 and fetches items.
 */
const onSearchHandler = () => {
  if (!selecting.value) {
    page.value = 1
    hasItems.value = false
    fetchItems()
  }
}

// Debounced version of the search handler
const debouncedSearchHandler = debounce(onSearchHandler, 500) // Adjust delay as needed


function onSubmitHandler() {
  if (!model.value) return
  
  const accountId = model.value.id
  window.location.href = `/accounts/${accountId}/dashboard`
}

return (_ctx: any,_cache: any) => {
  const _component_v_img = _resolveComponent("v-img")!
  const _component_v_list_item_title = _resolveComponent("v-list-item-title")!
  const _component_v_list_item = _resolveComponent("v-list-item")!
  const _component_v_icon = _resolveComponent("v-icon")!
  const _component_v_autocomplete = _resolveComponent("v-autocomplete")!
  const _component_v_btn = _resolveComponent("v-btn")!
  const _component_v_form = _resolveComponent("v-form")!
  const _component_v_card = _resolveComponent("v-card")!
  const _directive_intersect = _resolveDirective("intersect")!

  return (_openBlock(), _createElementBlock("div", {
    id: "default-layout",
    style: style
  }, [
    _createVNode(_component_v_card, {
      width: "400",
      class: "mx-auto px-10 py-8",
      elevation: "2"
    }, {
      default: _withCtx(() => [
        _createVNode(_component_v_img, {
          src: _unref(logo),
          alt: "Maropost logo",
          height: "60"
        }, null, 8, ["src"]),
        _cache[9] || (_cache[9] = _createElementVNode("div", { class: "my-6" }, [
          _createElementVNode("h4", { class: "font-weight-large" }, "To continue, Choose an account")
        ], -1)),
        _createVNode(_component_v_form, {
          modelValue: isFormValid.value,
          "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((isFormValid).value = $event)),
          onSubmit: _cache[3] || (_cache[3] = _withModifiers(() => {}, ["prevent"]))
        }, {
          default: _withCtx(() => [
            _createVNode(_component_v_autocomplete, _mergeProps({
              ref_key: "autocompleteRef",
              ref: autocompleteRef,
              search: search.value,
              "onUpdate:search": _cache[0] || (_cache[0] = ($event: any) => ((search).value = $event)),
              modelValue: model.value,
              "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((model).value = $event)),
              items: items.value,
              label: "Select an account",
              "item-value": "id",
              "item-title": "name",
              "append-outer-icon": isLoading.value ? 'mdi-loading' : '',
              loading: isLoading.value,
              "no-data-text": 'No record found'
            }, _ctx.$attrs, {
              rules: [required('Account')],
              "return-object": "",
              "onUpdate:focused": onFocusHandler,
              "onUpdate:search": _unref(debouncedSearchHandler),
              "onUpdate:modelValue": onModelUpdate
            }), {
              "prepend-item": _withCtx(() => [
                (isLoading.value)
                  ? (_openBlock(), _createBlock(_component_v_list_item, { key: 0 }, {
                      default: _withCtx(() => [
                        _createVNode(_component_v_list_item_title, null, {
                          default: _withCtx(() => [...(_cache[4] || (_cache[4] = [
                            _createTextVNode("Loading...", -1)
                          ]))]),
                          _: 1
                        })
                      ]),
                      _: 1
                    }))
                  : _createCommentVNode("", true)
              ]),
              "append-item": _withCtx(() => [
                (isLoadMoreEnabled.value)
                  ? _withDirectives((_openBlock(), _createBlock(_component_v_list_item, { key: 0 }, {
                      default: _withCtx(() => [
                        _createVNode(_component_v_list_item_title, null, {
                          default: _withCtx(() => [...(_cache[5] || (_cache[5] = [
                            _createTextVNode("Loading more...", -1)
                          ]))]),
                          _: 1
                        })
                      ]),
                      _: 1
                    })), [
                      [_directive_intersect, onIntersect]
                    ])
                  : _createCommentVNode("", true)
              ]),
              item: _withCtx(({ props, item }) => [
                _createVNode(_component_v_list_item, _normalizeProps(_guardReactiveProps(props)), {
                  prepend: _withCtx(() => [
                    (item.raw.id === accountId.value)
                      ? (_openBlock(), _createBlock(_component_v_icon, {
                          key: 0,
                          size: "24",
                          class: "mr-2",
                          style: {"color":"#f44336 !important"}
                        }, {
                          default: _withCtx(() => [...(_cache[6] || (_cache[6] = [
                            _createTextVNode(" mdi-alert-circle-outline ", -1)
                          ]))]),
                          _: 1
                        }))
                      : _createCommentVNode("", true)
                  ]),
                  _: 2
                }, 1040)
              ]),
              _: 1
            }, 16, ["search", "modelValue", "items", "append-outer-icon", "loading", "rules", "onUpdate:search"]),
            _createVNode(_component_v_btn, {
              variant: isFormValid.value ? 'flat' : 'outlined',
              density: "default",
              class: "text-body-2 text-uppercase mt-1 font-weight-bold w-100",
              "base-color": "black",
              disabled: !isFormValid.value,
              onClick: onSubmitHandler
            }, {
              default: _withCtx(() => [...(_cache[7] || (_cache[7] = [
                _createTextVNode(" Submit ", -1)
              ]))]),
              _: 1
            }, 8, ["variant", "disabled"]),
            _cache[8] || (_cache[8] = _createElementVNode("div", { class: "body-2 pt-4 text-medium-emphasis" }, [
              _createTextVNode("If the problem still persists "),
              _createElementVNode("a", {
                href: "mailto:support@maropost.com?subject=Support",
                target: "_blank"
              }, "Contact Support")
            ], -1))
          ]),
          _: 1
        }, 8, ["modelValue"])
      ]),
      _: 1
    })
  ]))
}
}

})